import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon,
  ListAlt as ListIcon,
  AddBox as AddBoxIcon,
  EditNote as EditNoteIcon,
  DeleteSweep as DeleteSweepIcon,
  CloudUpload as CloudUploadIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import type { HttpMethod, Priority } from "../types";
import { ModernMethodChip } from "./ModernUiComponents";

export interface EndpointTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  method: HttpMethod;
  priority: Priority;
  samplePayload: string;
  taskDescription: string;
  icon: React.ReactNode;
}

export const ENDPOINT_TEMPLATES: EndpointTemplate[] = [
  {
    id: "paginated-list",
    name: "Paginated List & Search API",
    category: "Read Operations",
    description: "Standard data listing endpoint with search query, filtering, page, and limit parameters.",
    method: "GET",
    priority: "medium",
    taskDescription: "Create a paginated listing endpoint supporting search query parameter, status filtering, and sorting order.",
    samplePayload: JSON.stringify(
      {
        page: 1,
        limit: 10,
        search: "",
        status_filter: "active",
        sort_by: "created_at",
        sort_order: "desc",
      },
      null,
      2
    ),
    icon: <ListIcon sx={{ fontSize: 28, color: "#0369A1" }} />,
  },
  {
    id: "create-entity",
    name: "Create / Register Entity API",
    category: "Write Operations",
    description: "Endpoint to create a new resource with field validation and returning created object ID.",
    method: "POST",
    priority: "high",
    taskDescription: "Create endpoint to register a new entity record. Should validate required fields and return 201 Created.",
    samplePayload: JSON.stringify(
      {
        name: "New Entity Name",
        code: "ENT-1001",
        category_id: 1,
        is_active: true,
        attributes: {
          tag: "sample",
        },
      },
      null,
      2
    ),
    icon: <AddBoxIcon sx={{ fontSize: 28, color: "#15803D" }} />,
  },
  {
    id: "partial-update",
    name: "Partial Update / Patch Entity",
    category: "Write Operations",
    description: "Update specific fields of an existing record without overwriting unmentioned properties.",
    method: "PATCH",
    priority: "medium",
    taskDescription: "Endpoint to update specific fields of a target resource. Accepts partial payload.",
    samplePayload: JSON.stringify(
      {
        name: "Updated Entity Name",
        status: "in_progress",
        remarks: "Updated via admin portal",
      },
      null,
      2
    ),
    icon: <EditNoteIcon sx={{ fontSize: 28, color: "#6B21A8" }} />,
  },
  {
    id: "bulk-delete",
    name: "Batch Delete / Remove Records",
    category: "Maintenance",
    description: "Endpoint to batch delete or archive multiple entity IDs in a single request.",
    method: "DELETE",
    priority: "high",
    taskDescription: "Bulk removal endpoint. Accepts array of entity IDs and returns count of deleted items.",
    samplePayload: JSON.stringify(
      {
        ids: [101, 102, 103],
        reason: "Clean up deprecated entries",
      },
      null,
      2
    ),
    icon: <DeleteSweepIcon sx={{ fontSize: 28, color: "#B91C1C" }} />,
  },
  {
    id: "file-upload",
    name: "File Attachment & Upload API",
    category: "Media / Storage",
    description: "Upload documents or screenshots returning stored file URL and metadata.",
    method: "POST",
    priority: "medium",
    taskDescription: "Multipart file upload endpoint. Should process document/image attachment and return hosted URL.",
    samplePayload: JSON.stringify(
      {
        file_name: "document.pdf",
        entity_id: "12345",
        file_category: "attachment",
      },
      null,
      2
    ),
    icon: <CloudUploadIcon sx={{ fontSize: 28, color: "#C2410C" }} />,
  },
];

interface EndpointTemplatesModalProps {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: EndpointTemplate) => void;
}

export const EndpointTemplatesModal: React.FC<EndpointTemplatesModalProps> = ({
  open,
  onClose,
  onSelectTemplate,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: "20px" } } }}>
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="800">
            Endpoint Architecture Templates
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select a preset template to auto-fill HTTP method, task requirements, and sample JSON payload schema
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={2.5}>
          {ENDPOINT_TEMPLATES.map((tmpl) => (
            <Grid size={{ xs: 12, md: 6 }} key={tmpl.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: "1px solid rgba(226, 232, 240, 0.9)",
                  backgroundColor: "background.paper",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 10px 24px -4px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => {
                  onSelectTemplate(tmpl);
                  onClose();
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ p: 1, borderRadius: "12px", backgroundColor: "#F8FAFC" }}>{tmpl.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="700">
                          {tmpl.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tmpl.category}
                        </Typography>
                      </Box>
                    </Box>
                    <ModernMethodChip method={tmpl.method} />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.4 }}>
                    {tmpl.description}
                  </Typography>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      backgroundColor: "#0F172A",
                      color: "#38BDF8",
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      maxHeight: 90,
                      overflow: "hidden",
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{tmpl.samplePayload}</pre>
                  </Box>
                </CardContent>

                <Box sx={{ px: 2.5, pb: 2, pt: 0, textAlign: "right" }}>
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
                  >
                    Use Template
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
